/**
 * Freeform live pitch surface for the lesson room: no target timeline, just
 * the incoming trace over a rolling window with an auto-scaled pitch axis.
 */

import { useEffect, useRef } from 'react';
import { midiToName, type TracePoint } from '@solfo/shared';

const TRACE = '#d85a30';

export default function LiveTraceCanvas({
  points,
  now,
  height = 220,
  windowSec = 8,
}: {
  points: TracePoint[];
  /** Latest stream time (right edge of the window). */
  now: number;
  height?: number;
  windowSec?: number;
}) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = height;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);

    const left = now - windowSec;
    const visible = points.filter((p) => p.t >= left);
    let lo = 48;
    let hi = 79;
    if (visible.length > 0) {
      lo = Math.floor(Math.min(...visible.map((p) => p.midi))) - 4;
      hi = Math.ceil(Math.max(...visible.map((p) => p.midi))) + 4;
      if (hi - lo < 12) {
        const mid = (hi + lo) / 2;
        lo = Math.floor(mid - 6);
        hi = Math.ceil(mid + 6);
      }
    }
    const y = (midi: number) => h - ((midi - lo) / (hi - lo)) * h;
    const x = (t: number) => ((t - left) / windowSec) * w;

    ctx.font = '10px system-ui';
    for (let m = Math.ceil(lo / 12) * 12; m <= hi; m += 12) {
      ctx.strokeStyle = '#f3efeb';
      ctx.beginPath();
      ctx.moveTo(0, y(m));
      ctx.lineTo(w, y(m));
      ctx.stroke();
      ctx.fillStyle = '#a89e94';
      ctx.fillText(midiToName(m), 4, y(m) - 3);
    }

    const traceTo = () => {
      ctx.beginPath();
      let pen = false;
      let prev: TracePoint | null = null;
      for (const p of visible) {
        const px = x(p.t);
        if (!pen || (prev && (p.t - prev.t > 0.25 || Math.abs(p.midi - prev.midi) > 7))) {
          ctx.moveTo(px, y(p.midi));
          pen = true;
        } else {
          ctx.lineTo(px, y(p.midi));
        }
        prev = p;
      }
    };
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    traceTo();
    ctx.strokeStyle = 'rgba(216,90,48,0.16)';
    ctx.lineWidth = 7;
    ctx.stroke();
    traceTo();
    ctx.strokeStyle = TRACE;
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [points, now, height, windowSec]);

  return (
    <canvas
      ref={ref}
      className="w-full rounded-xl bg-gradient-to-b from-white to-brand-50/50 ring-1 ring-brand-100/80"
      style={{ height }}
    />
  );
}
